import { useState } from 'react';
import { Stack, Text, PasswordInput, TextInput, Button, Group } from '@mantine/core';
import { ShieldSlash } from '@phosphor-icons/react';
import { useAdminTotpDisable } from '../../hooks/admin/useAdminUsers';
import { useAuthThrottle } from '../../hooks/useAuthThrottle';
import { parseApiError } from '../../utils/apiError';
import { ThrottleAlert, ErrorAlert } from './AuthFeedback';

/** POST /v1/admin/auth/totp/disable — revokes every session, so the caller ends up signed out. */
export function TotpDisableForm({ onDisabled, onCancel }: { onDisabled: () => void; onCancel?: () => void }) {
  const disable = useAdminTotpDisable();
  const throttle = useAuthThrottle();
  const [currentPassword, setCurrentPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPassword || !code.trim()) {
      setError('Enter your current password and an authenticator or recovery code.');
      return;
    }
    setError(null);
    disable.mutate(
      { currentPassword, code: code.trim() },
      {
        onSuccess: onDisabled,
        onError: (err) => {
          if (!throttle.handleError(err)) setError(parseApiError(err, 'Failed to disable 2FA').message);
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap={14}>
        <Text size="sm" c="var(--text-secondary)">
          Disabling two-factor authentication signs you out of every session, including this one.
        </Text>
        <ThrottleAlert throttle={throttle} />
        <ErrorAlert message={error} />
        <PasswordInput
          label="Current Password"
          required
          autoComplete="current-password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.currentTarget.value)}
        />
        <TextInput
          label="Authenticator code or recovery code"
          placeholder="123456 or xxxxxxxx-xxxxxxxx"
          required
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => setCode(e.currentTarget.value)}
          styles={{ input: { fontFamily: 'Geist Mono, monospace' } }}
        />
        <Group justify="flex-end">
          {onCancel && (
            <Button variant="subtle" onClick={onCancel} style={{ color: 'var(--text-secondary)' }}>
              Cancel
            </Button>
          )}
          <Button type="submit" color="red" loading={disable.isPending} disabled={throttle.isLocked}
            leftSection={<ShieldSlash size={14} />} fw={600}>
            Disable 2FA &amp; Sign Out
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
